import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@material-ui/core';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import {
  updateGeneralServiceSettings,
  getGeneralSetting,
  getPendingCoverageSubscribers,
} from 'src/redux/settings/action';
import { useToast } from 'src/contexts/toast';
import Autocomplete from '@material-ui/lab/Autocomplete';
import listaConcelhos from 'src/utils/concelhos';
import freguesias from 'src/utils/freguesias';

const ServiceSettings = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [settings, setSettings] = useState();

  const [selectedConcelhos, setSelectedConcelhos] = useState([]);
  const [useFreguesias, setUseFreguesias] = useState(false);
  const [selectedFreguesias, setSelectedFreguesias] = useState([]);
  const [freguesiasOptions, setFreguesiasOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const dataServices = await dispatch(getGeneralSetting());

      setSettings(dataServices.settings[0]);

      setLoading(false);

      setSelectedConcelhos(dataServices.array);
      
      // Load freguesias data if available in settings
      if (dataServices.freguesias && dataServices.freguesias.length > 0) {
        setUseFreguesias(true);
        setSelectedFreguesias(dataServices.freguesias);
      }
    };

    fetchData();
  }, [dispatch]);

  // Update freguesias options when concelhos change
  useEffect(() => {
    if (selectedConcelhos && selectedConcelhos.length > 0) {
      // Build freguesias list from selected concelhos
      const options = [];
      selectedConcelhos.forEach((concelho) => {
        const concelhoName = concelho.designacao;
        const freguesiasList = freguesias[concelhoName];
        
        if (freguesiasList) {
          freguesiasList.forEach((freguesia) => {
            options.push({
              designacao: `${concelhoName} - ${freguesia.designacao}`,
              concelho: concelhoName,
              freguesia: freguesia.designacao,
            });
          });
        }
      });
      setFreguesiasOptions(options);
    } else {
      setFreguesiasOptions([]);
    }
  }, [selectedConcelhos]);

  return (
    <Container maxWidth={false}>
      <Box paddingTop={3}>
        <Grid container spacing={2} justifyContent="flex-start">
          <Grid item xs={12} sm={8}>
            <Card style={{ display: 'flex' }}>
              <CardHeader title="Edit Service" />
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Box paddingTop={3}>
        <Grid container spacing={2} justifyContent="flex-start">
          <Grid item xs={12} sm={8}>
            <Card>
              <CardContent>
                {loading && <CircularProgress />}
                {!loading && (
                  <Grid container spacing={2} justifyContent="flex-start">
                    <Grid item xs={12} sm={12}>
                      <Formik
                        enableReinitialize
                        initialValues={{
                          commission_perc: settings
                            ? settings.commission_perc
                            : '',
                          extra_cost: settings ? settings.extra_cost : '',
                          cancel_price: settings ? settings.cancel_price : '',
                          cancel_time: settings ? settings.cancel_time : '',
                          max_req_radius: settings
                            ? settings.max_req_radius
                            : '',
                        }}
                        validationSchema={Yup.object().shape({
                          commission_perc: Yup.number().required(
                            'Default commission is required'
                          ),
                          extra_cost: Yup.number().required(
                            'Default extra price is required'
                          ),
                          cancel_price: Yup.number().required(
                            'Default calcelation price is required'
                          ),
                          cancel_time: Yup.number().required(
                            'Default cancelation time is required'
                          ),
                          max_req_radius: Yup.number().required(
                            'Default cancelation time is required'
                          ),
                        })}
                        onSubmit={async (values) => {
                          // onSubmit={async (values) => {
                          try {
                            // Detect newly added regions by diffing current settings
                            // against the form selection. Only check coverage subs
                            // for additions so we don't spam on every edit.
                            const previousArray = (settings && settings.regions
                              ? String(settings.regions).split(';').map((d) => ({ designacao: d }))
                              : []);
                            const prevKeys = new Set(previousArray.map((c) => (c.designacao || '').toLowerCase()));
                            const newConcelhos = (selectedConcelhos || []).filter(
                              (c) => !prevKeys.has((c.designacao || '').toLowerCase())
                            );
                            const newFreguesias = useFreguesias
                              ? selectedFreguesias.filter(
                                  (f) => !prevKeys.has((f.designacao || '').toLowerCase())
                                )
                              : [];
                            const candidateRegions = [
                              ...newConcelhos.map((c) => ({ concelho: c.designacao, freguesia: null })),
                              ...newFreguesias.map((f) => ({ concelho: f.concelho, freguesia: f.freguesia })),
                            ];

                            let pendingTotal = 0;
                            for (const r of candidateRegions) {
                              try {
                                const d = await dispatch(getPendingCoverageSubscribers(r));
                                pendingTotal += Number(d && d.count) || 0;
                              } catch (_) { /* ignore */ }
                            }

                            let notify = false;
                            if (pendingTotal > 0) {
                              // eslint-disable-next-line no-alert
                              notify = window.confirm(
                                `${pendingTotal} customer(s) asked to be notified when one of the newly added regions becomes available. Send them a notification now?`
                              );
                            }

                            const payload = {
                              values,
                              concelhos: selectedConcelhos,
                              useFreguesias,
                              freguesias: useFreguesias ? selectedFreguesias : [],
                              notifySubscribers: notify,
                            };
                            const result = await dispatch(
                              updateGeneralServiceSettings(payload)
                            );
                            const sent = result && result.notify_result
                              ? result.notify_result.regions.reduce((a, x) => a + (x.sent || 0), 0)
                              : 0;
                            showToast(
                              sent > 0
                                ? `Service settings updated. Notified ${sent} subscriber(s).`
                                : 'Service settings updated successfully!'
                            );
                          } catch (error) {
                            console.error(error.message);
                          }
                        }}
                      >
                        {({
                          errors,
                          handleBlur,
                          handleChange,
                          handleSubmit,
                          isSubmitting,
                          touched,
                          values,
                        }) => (
                          <form onSubmit={handleSubmit}>
                            <TextField
                              error={Boolean(
                                touched.commission_perc &&
                                  errors.commission_perc
                              )}
                              fullWidth
                              helperText={
                                touched.commission_perc &&
                                errors.commission_perc
                              }
                              label="Default comission (%)"
                              margin="normal"
                              name="commission_perc"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.commission_perc}
                              variant="outlined"
                              type="number"
                            />
                            <TextField
                              error={Boolean(
                                touched.extra_cost && errors.extra_cost
                              )}
                              fullWidth
                              helperText={
                                touched.extra_cost && errors.extra_cost
                              }
                              label="Default extra cost (Euros €)"
                              margin="normal"
                              name="extra_cost"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.extra_cost}
                              variant="outlined"
                              type="number"
                            />
                            <TextField
                              error={Boolean(
                                touched.cancel_price && errors.cancel_price
                              )}
                              fullWidth
                              helperText={
                                touched.cancel_price && errors.cancel_price
                              }
                              label="Default cancelation price (Euros €)"
                              margin="normal"
                              name="cancel_price"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.cancel_price}
                              variant="outlined"
                              type="number"
                            />
                            <TextField
                              error={Boolean(
                                touched.cancel_time && errors.cancel_time
                              )}
                              fullWidth
                              helperText={
                                touched.cancel_time && errors.cancel_time
                              }
                              label="Default cancelation time (seconds)"
                              margin="normal"
                              name="cancel_time"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.cancel_time}
                              variant="outlined"
                              type="number"
                            />
                            <TextField
                              error={Boolean(
                                touched.max_req_radius && errors.max_req_radius
                              )}
                              fullWidth
                              helperText={
                                touched.max_req_radius && errors.max_req_radius
                              }
                              label="Max Request Radius(Km) (Distance b/t Pickup point and Driver location)"
                              margin="normal"
                              name="max_req_radius"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="number"
                              value={values.max_req_radius}
                              variant="outlined"
                            />

                            <Grid item xs={12} md={12} sm={12}>
                              <Typography variant="h6" gutterBottom>
                                Service Regions
                              </Typography>
                              
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={useFreguesias}
                                    onChange={(e) => {
                                      setUseFreguesias(e.target.checked);
                                      if (!e.target.checked) {
                                        setSelectedFreguesias([]);
                                      }
                                    }}
                                    color="primary"
                                  />
                                }
                                label="Use Freguesia-level restrictions"
                                style={{ marginBottom: 16 }}
                              />

                              <Autocomplete
                                multiple
                                id="concelhos-selector"
                                options={listaConcelhos || []}
                                value={selectedConcelhos}
                                getOptionSelected={(option, value) =>
                                  option.designacao === value.designacao
                                }
                                getOptionLabel={(option) => option.designacao}
                                onChange={(e, value) => {
                                  setSelectedConcelhos(value);
                                  // Clear freguesias if concelhos change
                                  if (useFreguesias) {
                                    setSelectedFreguesias([]);
                                  }
                                }}
                                filterSelectedOptions
                                endAdornment={false}
                                renderInput={(param) => (
                                  <TextField
                                    {...param}
                                    variant="outlined"
                                    label={
                                      useFreguesias
                                        ? 'Select Concelhos (filter for freguesias)'
                                        : 'Select Regions (Concelhos)'
                                    }
                                    placeholder="Select concelhos"
                                  />
                                )}
                              />

                              {useFreguesias && (
                                <Box mt={2}>
                                  <Autocomplete
                                    multiple
                                    id="freguesias-selector"
                                    options={freguesiasOptions}
                                    value={selectedFreguesias}
                                    disabled={
                                      !selectedConcelhos ||
                                      selectedConcelhos.length === 0
                                    }
                                    groupBy={(option) => option.concelho}
                                    getOptionSelected={(option, value) =>
                                      option.designacao === value.designacao
                                    }
                                    getOptionLabel={(option) =>
                                      option.designacao
                                    }
                                    onChange={(e, value) => {
                                      setSelectedFreguesias(value);
                                    }}
                                    filterSelectedOptions
                                    renderInput={(param) => (
                                      <TextField
                                        {...param}
                                        variant="outlined"
                                        label="Select Freguesias"
                                        placeholder="Select freguesias from chosen concelhos"
                                        helperText={
                                          selectedConcelhos.length === 0
                                            ? 'Please select concelhos first'
                                            : `${freguesiasOptions.length} freguesias available from ${selectedConcelhos.length} concelho(s)`
                                        }
                                      />
                                    )}
                                  />
                                  {selectedFreguesias.length === 0 &&
                                    selectedConcelhos.length > 0 && (
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{ marginTop: 8, display: 'block' }}
                                      >
                                        Note: Empty freguesia selection means all
                                        freguesias in selected concelhos are allowed
                                      </Typography>
                                    )}
                                </Box>
                              )}
                            </Grid>
                            <Box paddingY={3}>
                              <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                  <Button
                                    color="primary"
                                    disabled={isSubmitting}
                                    size="large"
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                  >
                                    UPDATE
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          </form>
                        )}
                      </Formik>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ServiceSettings;
